import { useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api, errorText } from '../api/client'
import type { Channel, ContactDetail, ContactMethod, ProfilePatch, User } from '../api/types'
import { Composer } from '../components/Composer'
import { Icon } from '../components/Icon'
import { useConfirm } from '../components/Confirm'
import { ConsentModal } from '../components/ConsentModal'
import { OUTREACH, reachable, useOutreach } from '../components/Outreach'
import { ExportToLosModal } from '../components/ExportToLosModal'
import { IntakeFields, useVocabulary } from '../components/IntakeFields'
import { Modal } from '../components/Modal'
import { NewDealModal } from '../components/NewDealModal'
import { FilesPanel, NotesPanel } from '../components/NotesPanel'
import { AppointmentsPanel } from '../components/AppointmentsPanel'
import { Relationships } from '../components/Relationships'
import { SocialLinks } from '../components/SocialLinks'
import { EnrollmentList, PlaybookModal } from '../components/Playbooks'
import { ErrorState, Loading } from '../components/States'
import { StatusMenu } from '../components/StatusMenu'
import { TagEditor } from '../components/TagEditor'
import { TaskList } from '../components/TaskList'
import { Timeline } from '../components/Timeline'
import { useToast } from '../components/Toast'
import { LEAD_STATUSES } from '../lib/constants'
import { initials, relativeDays, titleCase, usd, usdShort } from '../lib/format'
import { tint } from '../lib/tint'
import { useAsync } from '../lib/useAsync'

interface Reason { factor: string; value: string; detail: string; impact: string }

function reasonsOf(explain: unknown): Reason[] {
  const r = (explain as { reasons?: unknown } | null)?.reasons
  return Array.isArray(r) ? (r as Reason[]).filter((x) => x && typeof x.factor === 'string') : []
}

const IMPACT_COLOR: Record<string, string> = {
  high: 'var(--positive)', med: 'var(--accent)', low: 'var(--ink-3)', info: 'var(--ink-3)',
}

function formatPhone(v: string) {
  const d = v.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '')
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : v
}

const CHANNEL_LABEL: Record<Channel, string> = {
  call: 'Call', sms: 'Text', email: 'Email', mail: 'Mail', whatsapp: 'WhatsApp',
}

/** The contact card. Who they are and how to reach them come first; what they're
 *  worth as a lead, what's happened, and what's next follow. Loans appear only
 *  when there are any — most people in the lead book don't have one yet. */
export function Contact360({ user }: { user: User }) {
  const { contactId = '' } = useParams()
  const id = Number(contactId)
  const [bump, setBump] = useState(0)
  const { data, error, loading, reload } = useAsync(() => api.contact(id), [id])
  // Declared before the loading guard, because a hook cannot be conditional. The
  // lead page calls the identical one.
  const outreach = useOutreach(id, data?.full_name ?? 'this contact', {
    phone: data?.methods.find((m) => m.kind === 'mobile' || m.kind === 'landline')?.value,
    onChanged: reload,
  })
  const [local, setLocal] = useState<ContactDetail | null>(null)
  const c = local && local.id === id ? local : data
  const [editing, setEditing] = useState(false)
  const [playbook, setPlaybook] = useState(false)
  const [deal, setDeal] = useState(false)
  const [tab, setTab] = useState<'activity' | 'tasks' | 'meetings' | 'files'>('activity')
  const [editingGoal, setEditingGoal] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [consenting, setConsenting] = useState(false)
  const toast = useToast()
  const nav = useNavigate()
  const vocabulary = useVocabulary()

  const refresh = () => {
    setLocal(null)
    reload()
    setBump((n) => n + 1)
  }

  if (!Number.isFinite(id)) return <div className="page"><ErrorState error={new Error('Bad contact link')} /></div>
  if (loading && !c) return <div className="page"><Loading /></div>
  if (error || !c) return <div className="page"><ErrorState error={error} onRetry={reload} /></div>

  const name = c.full_name || [c.first_name, c.last_name].filter(Boolean).join(' ') || 'Unnamed contact'
  const phones = c.methods.filter((m) => m.kind !== 'email')
  const emails = c.methods.filter((m) => m.kind === 'email')
  const mobile = phones.find((m) => m.kind === 'mobile' && !m.dnc) ?? phones.find((m) => !m.dnc)
  const email = emails.find((m) => !m.dnc)
  const reasons = reasonsOf(c.explain)
  const score = (c.explain as { score?: number } | null)?.score ?? c.rank
  const signals = (c.signals ?? {}) as Record<string, unknown>
  const equity = [signals.attom_equity, signals.usable_equity].find((v) => typeof v === 'number') as number | undefined
  const subject = { contact_id: c.id }
  // Why the campaign controls are off, in the words of the setting that turns them
  // off — the officer owns that setting, so the message points at it.
  const campaignsNote = c.permissions.campaigns
    ? 'Start one of your workflows for this contact'
    : `${c.role_label ?? 'This role'} is set not to receive campaigns — change it in Settings → roles.`
  const profile = (c.profile ?? {}) as Record<string, string | number | boolean | null>
  // The export has nothing to say about a contact with no purpose, no amount and
  // no opportunity — which is every title company and most new contacts.
  const canExport = Boolean(profile.purpose || profile.loan_amount || c.deals.length
    || (c.signals as Record<string, unknown> | null)?.usable_equity)
  const hasGoal = Object.values(profile).some(
    (v) => v !== null && v !== '' && v !== undefined && typeof v !== 'object')
  const label = (options: { key: string; label: string }[] | undefined, key: unknown) =>
    options?.find((o) => o.key === key)?.label ?? (key ? titleCase(String(key)) : null)
  const goalRows: [string, string][] = ([
    ['Purpose', label(vocabulary?.purpose, profile.purpose)],
    ['Timeline', label(vocabulary?.timeline, profile.timeline)],
    [profile.purpose === 'purchase' ? 'Target price' : 'Home value',
      profile.price_or_value ? usd(Number(profile.price_or_value)) : null],
    ['Loan amount', profile.loan_amount ? usd(Number(profile.loan_amount)) : null],
    ['Down payment', profile.down_payment ? usd(Number(profile.down_payment)) : null],
    ['Credit', label(vocabulary?.credit_band, profile.credit_band)],
    ['Property use', label(vocabulary?.property_use, profile.property_use)],
    ['First-time buyer', profile.first_time_buyer ? 'Yes' : null],
    ['Source', label(vocabulary?.source, profile.source)],
    ['Referred by', (profile.referred_by_name as string) || null],
    ['Co-borrower', (profile.co_borrower_name as string) || null],
  ] as [string, string | null][]).filter((row): row is [string, string] => Boolean(row[1]))

  async function setStatus(status: string) {
    if (!c || status === c.status) return
    try {
      setLocal(await api.updateContact(c.id, { status }))
      setBump((n) => n + 1)
      toast(`Moved to ${titleCase(status)}`, { tone: 'positive' })
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    }
  }


  return (
    <div className="page contact-workspace">
      <div className="crumbs">
        <Link to="/contacts">Contacts</Link> › {name}
      </div>

      <div className="card contact-header">
        <div className="detail-head">
          <span className="detail-av" style={tint(name)}>{initials(name)}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 className="h1 contact-name">{name}</h1>
            <div className="contact-header-meta">
              <StatusMenu value={c.status} label="Lead stage" options={LEAD_STATUSES.some(s=>s.key===c.status) ? LEAD_STATUSES : [...LEAD_STATUSES,{key:c.status,label:titleCase(c.status)}]} onChange={setStatus} />
              {c.role_label ? (
                <span className="chip-outline" title={c.entity_kind === 'company' ? 'A firm, not a person' : undefined}>
                  {c.entity_kind === 'company' ? <Icon name="domain" size={13} style={{ verticalAlign: -2 }} /> : null}
                  {' '}{c.role_label}
                </span>
              ) : null}
              {c.loans.length ? <span className="chip-outline">On {c.loans.length} loan{c.loans.length === 1 ? '' : 's'}</span> : null}
            </div>
            <p className="subline" style={{ fontSize: 13 }}>
              {[mobile ? formatPhone(mobile.value) : null, email?.value,
                [c.address, c.city, c.state].filter(Boolean).join(', ') || null].filter(Boolean).join(' · ') || 'No contact details yet'}
            </p>
            <div style={{ marginTop: 9 }}>
              <TagEditor subject={subject} tags={c.tags} onChange={(tags) => setLocal({ ...c, tags })} />
            </div>
          </div>
          {/* Three groups, not one row of peers: reach them now, put them in
              something that runs on its own, hand them to the LOS. They were
              sitting side by side as though they were alternatives to each other. */}
          <div className="contact-header-actions">
            <span className="ha-group">
            {/* The same four, in the same order, with the same gate and the same
                dialogs as the lead page — one wiring, so learning one page is not
                being wrong about the other. */}
            {OUTREACH.map((ch) => {
              const { ok, why } = reachable(c.channels[ch.key])
              return (
                <button key={ch.key} className="iconbtn lg" disabled={!ok}
                  title={ok ? ch.label : `${ch.label} — ${why}`}
                  onClick={() => outreach.start(ch.key)}>
                  <Icon name={ch.icon} size={18} />
                </button>
              )
            })}
            </span>

            <span className="ha-sep" />

            <span className="ha-group">
            {/* Shown for every role. Where a campaign may not reach this one these
                are disabled and say why, rather than vanishing: a control nobody
                can see reads as a feature that is missing, and the permission
                behind it is one the officer sets themselves. */}
            <button className="btn primary" onClick={() => setPlaybook(true)}
              disabled={!c.permissions.campaigns} title={campaignsNote}>
              <Icon name="bolt" size={17} /> Start workflow
            </button>
            </span>

            <span className="ha-sep" />

            {/* Every contact can be handed to LendingPad, whatever their role and
                whether or not an intake was ever filled in — the dialog is the
                thing that knows what the file still needs. It lived inside the
                Loan goal card, which only appears for some roles and only once
                someone had recorded a purpose. */}
            <button className="btn los" onClick={() => setExporting(true)} disabled={!canExport}
              title={canExport ? 'Create a MISMO 3.4 file for this contact'
                : 'A file needs a loan purpose and an amount. Add them under Loan goal, or open an opportunity for them.'}>
              <Icon name="upload_file" size={17} /> To LendingPad
            </button>
          </div>
        </div>
        {!c.permissions.campaigns ? (
          <p className="ha-note">
            <Icon name="info" size={14} />
            <span>
              {c.role_label ?? 'This role'} is set not to receive campaigns, so no workflow
              can run for them. <Link to="/customize">Change it in Settings → roles</Link>.
            </span>
          </p>
        ) : null}
      </div>

      <div className="body-3col" style={{ marginTop: 16 }}>
        <div className="stack">
          <section className="card">
            <div className="card-head">
              <h2 className="h2">Contact info</h2>
              <span className="spacer" />
              <button className="btn sm" onClick={() => setEditing(true)}><Icon name="edit" size={15} /> Edit</button>
            </div>
            <div className="card-body" style={{ paddingTop: 4 }}>
              {/* The lead book can flag several methods primary; badge only the first per kind. */}
              {[...phones, ...emails].map((m, i, all) => (
                <MethodRow key={`${m.kind}-${m.value}`} m={m}
                  primary={m.is_primary && all.findIndex((x) => x.kind === m.kind && x.is_primary) === i} />
              ))}
              <AddMethod contactId={c.id} onAdded={(d) => setLocal(d)} />
              <div className="kvrow"><span className="k">Address</span>
                <span className="v">
                  {c.address ? <>{c.address}<br />{[c.city, c.state, c.zip].filter(Boolean).join(', ')}</> : '—'}
                </span></div>
              <div className="kvrow"><span className="k">Owner</span>
                <span className="v">{c.owner ?? 'Unassigned'}</span></div>
              <div className="kvrow"><span className="k">Source</span>
                <span className="v">{titleCase(c.source)}{c.campaign ? ` · ${c.campaign}` : ''}</span></div>
              {c.business_card ? (
                <div className="kvrow">
                  <span className="k">Business card</span>
                  <span className="v"><BusinessCardLink card={c.business_card} name={c.full_name} /></span>
                </div>
              ) : null}
              <SocialLinks contactId={c.id} profile={c.profile} onSaved={refresh} />
              <div className="kvrow"><span className="k">In stage</span>
                <span className="v">{relativeDays(c.status_at ?? c.created_at)}</span></div>
            </div>
          </section>

          <section className="card">
            <div className="card-head"><h2 className="h2">Can we reach them?</h2></div>
            <div className="card-body" style={{ paddingTop: 4 }}>
              {(['call', 'sms', 'email', 'mail'] as Channel[]).map((ch) => {
                const s = c.channels[ch]
                if (!s) return null
                const said = c.consent?.[ch === 'call' ? 'voice' : ch]?.status
                return (
                  <div className="reach-row" key={ch}>
                    <span className="k">{CHANNEL_LABEL[ch]}</span>
                    {/* Why it's shut, where the reason isn't the switch itself —
                        no number on file, a bounced address, the DNC registry. */}
                    <span className="reach-why">
                      {said === 'opted_out' || s.allowed ? '' : s.reason}
                    </span>
                    <ChannelSwitch contactId={c.id} channel={ch} consent={said} open={s.allowed}
                      onOptIn={() => setConsenting(true)} onSaved={refresh} />
                  </div>
                )
              })}
            </div>
          </section>

        </div>

        <div className="stack">
        <Relationships contactId={c.id} name={c.full_name} />

        <section className="card notes-card">
          <div className="card-head">
            <h2 className="h2">Notes</h2>
            {c.note_count ? <span className="count-pill">{c.note_count}</span> : null}
          </div>
          <div className="card-body" style={{ paddingTop: 2 }}>
            <NotesPanel subject={subject} onChange={refresh} />
          </div>
        </section>

        <section className="card">
          <div style={{ padding: '4px 18px 0' }}>
            <div className="tabs" style={{ marginTop: 8 }}>
              <button className={`tab${tab === 'activity' ? ' active' : ''}`} onClick={() => setTab('activity')}>Activity</button>
              <button className={`tab${tab === 'tasks' ? ' active' : ''}`} onClick={() => setTab('tasks')}>
                Tasks{c.open_tasks ? <span className="count">{c.open_tasks}</span> : null}
              </button>
              <button className={`tab${tab === 'meetings' ? ' active' : ''}`} onClick={() => setTab('meetings')}>
                Meetings
              </button>
              <button className={`tab${tab === 'files' ? ' active' : ''}`} onClick={() => setTab('files')}>
                Files{c.file_count ? <span className="count">{c.file_count}</span> : null}
              </button>
            </div>
          </div>
          <div className="card-body">
            {tab === 'activity' ? (
              <>
                <Composer subject={subject} onLogged={() => setBump((n) => n + 1)} />
                <Timeline subject={subject} refreshKey={bump} emptyText="No history yet. Log a call or note above." />
              </>
            ) : tab === 'tasks' ? (
              <TaskList subject={subject} refreshKey={bump} onChange={refresh} />
            ) : tab === 'meetings' ? (
              <AppointmentsPanel subject={subject} refreshKey={bump} onChange={refresh} />
            ) : (
              <FilesPanel subject={subject} onChange={refresh} />
            )}
          </div>
        </section>
        </div>

        <div className="stack">
          {/* Only where the role says these questions apply — a title company
              has no loan purpose or credit band. */}
          {c.permissions.intake ? (
          <section className="card">
            <div className="card-head">
              <h2 className="h2">Loan goal</h2>
              <span className="spacer" />
              {/* Hand the lead to LendingPad as a 3.4 file without opening an
                  opportunity first — intake is enough to start an application. */}
              <button className="btn sm" onClick={() => setEditingGoal(true)}>
                <Icon name="edit" size={15} /> {hasGoal ? 'Edit' : 'Add'}
              </button>
            </div>
            <div className="card-body" style={{ paddingTop: 4 }}>
              {hasGoal ? (
                <>
                  {goalRows.map(([k, v]) => (
                    <div className="kvrow" key={k}>
                      <span className="k">{k}</span>
                      <span className="v">{v}</span>
                    </div>
                  ))}
                  {profile.goal_notes ? (
                    <p className="tl-body" style={{ marginTop: 8 }}>{profile.goal_notes}</p>
                  ) : null}
                </>
              ) : (
                <p className="quiet" style={{ margin: '4px 0' }}>
                  Nothing asked yet. What they want and when is what decides who you call first.
                </p>
              )}
            </div>
          </section>
          ) : null}

          <section className="card">
            <div className="card-head">
              <h2 className="h2">Lead signals</h2>
              <span className="spacer" />
              {score !== null && score !== undefined ? <span className="chip-mono" title="Lead score">{score}</span> : null}
            </div>
            <div className="card-body" style={{ paddingTop: 4 }}>
              {equity !== undefined ? (
                <div className="kvrow"><span className="k">Usable equity</span><span className="v">{usdShort(equity)}</span></div>
              ) : null}
              {c.product ? (
                <div className="kvrow"><span className="k">Product fit</span>
                  <span className="v">{titleCase(c.product)}</span></div>
              ) : null}
              {reasons.length ? reasons.map((r) => (
                <div key={r.factor} style={{ padding: '8px 0', borderBottom: '1px solid var(--line-faint)' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: IMPACT_COLOR[r.impact] ?? 'var(--ink-3)', flex: 'none', transform: 'translateY(-1px)' }} />
                    <span style={{ fontSize: 12.5, fontWeight: 600, flex: 1 }}>{r.factor}</span>
                    <span className="mono" style={{ fontSize: 12 }}>{r.value}</span>
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2, paddingLeft: 14 }}>{r.detail}</div>
                </div>
              )) : (
                <p className="quiet" style={{ margin: '6px 0 0' }}>
                  {c.source === 'manual' || c.source === 'import'
                    ? 'Added by hand, so there is no property-based score. Signals appear for contacts matched to a parcel.'
                    : 'No scoring factors recorded for this contact.'}
                </p>
              )}
            </div>
          </section>

          {/* Roles that turn up on a file. A lender AE never does. */}
          {c.permissions.loan_party ? (
          <section className="card">
            <div className="card-head">
              <h2 className="h2">Loans</h2>
              <span className="spacer" />
              <button className="btn sm" onClick={() => setDeal(true)}><Icon name="add" size={15} /> Opportunity</button>
            </div>
            <div className="card-body" style={{ paddingTop: 4 }}>
              {c.loans.length === 0 && c.deals.length === 0 ? (
                <p className="quiet" style={{ margin: '4px 0' }}>No loan yet. Create an opportunity when they're ready to talk numbers.</p>
              ) : null}
              {c.deals.map((d) => (
                <button key={d.loan_id} className="condrow" style={{ width: '100%', border: 0, background: 'none', cursor: 'pointer', textAlign: 'left' }}
                  onClick={() => nav(`/loans/${encodeURIComponent(d.loan_id)}`)}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, display: 'block' }}>{titleCase(d.purpose)}</span>
                    <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>Pre-LOS · {titleCase(d.stage)}</span>
                  </span>
                  <span className="mono" style={{ fontSize: 12.5 }}>{usd(d.loan_amount)}</span>
                </button>
              ))}
              {c.loans.map((l) => (
                <button key={`${l.loan_id}-${l.role}`} className="condrow" style={{ width: '100%', border: 0, background: 'none', cursor: 'pointer', textAlign: 'left' }}
                  onClick={() => nav(`/loans/${encodeURIComponent(l.loan_id)}`)}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, display: 'block' }}>
                      {l.property_street ?? titleCase(l.purpose)}
                    </span>
                    <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                      {titleCase(l.role)} · {titleCase(l.status)}{l.loan_number ? ` · ${l.loan_number}` : ''}
                    </span>
                  </span>
                  <span className="mono" style={{ fontSize: 12.5 }}>{usd(l.loan_amount)}</span>
                </button>
              ))}
            </div>
          </section>

          ) : null}

          <section className="card">
            <div className="card-head">
              <h2 className="h2">Workflows</h2>
              <span className="spacer" />
              <button className="btn sm" onClick={() => setPlaybook(true)}
                disabled={!c.permissions.campaigns} title={campaignsNote}>
                <Icon name="add" size={15} /> Add
              </button>
            </div>
            <div className="card-body" style={{ paddingTop: 4 }}>
              {c.permissions.campaigns ? (
                <EnrollmentList subject={subject} refreshKey={bump} onChange={() => setBump((n) => n + 1)}
                  onAdd={() => setPlaybook(true)} />
              ) : (
                <p className="quiet" style={{ margin: 0, fontSize: 12.5 }}>
                  {c.role_label ?? 'This role'} is set not to receive campaigns, so no workflow can
                  run for them. Change it under <Link to="/customize">Settings → roles</Link>.
                </p>
              )}
            </div>
          </section>
        </div>
      </div>

      {editingGoal ? (
        <GoalModal contact={c} vocabulary={vocabulary} onClose={() => setEditingGoal(false)}
          onSaved={(d) => { setLocal(d); setEditingGoal(false); setBump((n) => n + 1) }} />
      ) : null}
      {outreach.modals}

      {consenting ? (
        <ConsentModal contactId={c.id} name={name} channel="sms"
          current={c.consent?.sms?.status}
          onClose={() => setConsenting(false)} onSaved={refresh} />
      ) : null}

      {exporting ? (
        <ExportToLosModal subject={{ contact_id: c.id }} onClose={() => setExporting(false)}
          onPushed={() => setBump((n) => n + 1)} />
      ) : null}
      {editing ? (
        <EditContactModal c={c} canDelete={user.is_admin || c.created_by === user.lo_id}
          onClose={() => setEditing(false)}
          onSaved={(d) => { setLocal(d); setEditing(false) }}
          onDeleted={() => nav('/contacts')} />
      ) : null}
      <PlaybookModal open={playbook} onClose={() => setPlaybook(false)} subject={subject} onEnrolled={refresh}
        firstName={c.first_name || name.split(' ')[0]} />
      <NewDealModal open={deal} onClose={() => setDeal(false)} contactId={c.id} borrowerName={name} />
    </div>
  )
}

function MethodRow({ m, primary }: { m: ContactMethod; primary: boolean }) {
  const icon = m.kind === 'email' ? 'mail' : m.kind === 'mobile' ? 'smartphone' : 'call'
  const value = m.kind === 'email' ? m.value : formatPhone(m.value)
  const href = m.kind === 'email' ? `mailto:${m.value}` : `tel:${m.value.replace(/[^\d+]/g, '')}`
  return (
    <div className="kvrow" style={{ alignItems: 'center' }}>
      <span className="k" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Icon name={icon} size={15} /> {titleCase(m.kind)}
      </span>
      <span className="v" style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flexWrap: 'wrap' }}>
        <a href={href} className="method-value">{value}</a>
        {primary ? <span className="badge t-neutral">Primary</span> : null}
        {m.dnc ? <span className="badge t-danger" title="On a do-not-call list">DNC</span> : null}
      </span>
    </div>
  )
}

function AddMethod({ contactId, onAdded }: { contactId: number; onAdded: (d: ContactDetail) => void }) {
  const [open, setOpen] = useState(false)
  const [kind, setKind] = useState<'mobile' | 'landline' | 'email'>('mobile')
  const [value, setValue] = useState('')
  const toast = useToast()

  async function save(e: React.FormEvent) {
    e.preventDefault()
    try {
      onAdded(await api.addMethod(contactId, { kind, value }))
      setValue('')
      setOpen(false)
    } catch (err) {
      toast(errorText(err), { tone: 'danger' })
    }
  }

  if (!open) {
    return <button className="tag-add" style={{ margin: '8px 0' }} onClick={() => setOpen(true)}>＋ Phone or email</button>
  }
  return (
    <form onSubmit={save} style={{ display: 'flex', gap: 6, margin: '8px 0' }}>
      <select className="field" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)} style={{ width: 100 }} aria-label="Type">
        <option value="mobile">Mobile</option><option value="landline">Landline</option><option value="email">Email</option>
      </select>
      <input className="field" autoFocus value={value} onChange={(e) => setValue(e.target.value)} style={{ flex: 1 }}
        type={kind === 'email' ? 'email' : 'tel'} aria-label="Value" />
      <button className="btn sm" disabled={!value.trim()}>Add</button>
    </form>
  )
}

function EditContactModal({ c, canDelete, onClose, onSaved, onDeleted }: {
  c: ContactDetail; canDelete: boolean; onClose: () => void
  onSaved: (d: ContactDetail) => void; onDeleted: () => void
}) {
  const [form, setForm] = useState({
    first_name: c.first_name ?? '', last_name: c.last_name ?? '', address: c.address ?? '',
    city: c.city ?? '', state: c.state ?? '', zip: c.zip ?? '', owner_lo_id: c.owner_lo_id ?? '',
  })
  const [busy, setBusy] = useState(false)
  const { data: officers } = useAsync(() => api.officers().catch(() => ({ officers: [] })), [])
  const toast = useToast()
  const confirm = useConfirm()
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  async function save() {
    setBusy(true)
    try {
      const patch = { ...form, owner_lo_id: form.owner_lo_id === '' ? undefined : Number(form.owner_lo_id) }
      if (patch.owner_lo_id === c.owner_lo_id) delete patch.owner_lo_id
      onSaved(await api.updateContact(c.id, patch))
      toast('Saved', { tone: 'positive' })
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  /** Not an archive. The record and its whole history go — see writes.delete_contact,
   *  which refuses anyone who is a party on a loan file. */
  async function remove() {
    if (!await confirm({
      title: `Delete ${c.full_name ?? 'this contact'}?`,
      body: 'Their messages, calls, consent record, notes, tasks and timeline go with them. '
        + 'This cannot be undone, and it is not how you retire someone who has gone quiet — '
        + 'set their status to Dead for that.',
      confirmLabel: 'Delete them', danger: true,
    })) return
    setBusy(true)
    try {
      await api.deleteContact(c.id)
      toast('Contact deleted', { tone: 'positive' })
      onDeleted()
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
      setBusy(false)
    }
  }

  return (
    <ModalShell onClose={onClose} onSave={save} busy={busy}
      extra={canDelete ? (
        <button className="btn danger" onClick={remove} disabled={busy}>
          <Icon name="delete" size={16} /> Delete contact
        </button>
      ) : null}>
      <div className="form-grid">
        <label className="fl"><span>First name</span><input className="field" value={form.first_name} onChange={set('first_name')} /></label>
        <label className="fl"><span>Last name</span><input className="field" value={form.last_name} onChange={set('last_name')} /></label>
        <label className="fl span-2"><span>Street address</span><input className="field" value={form.address} onChange={set('address')} /></label>
        <div className="span-2" style={{ display: 'grid', gridTemplateColumns: '1.6fr 0.5fr 0.8fr', gap: 10 }}>
          <label className="fl"><span>City</span><input className="field" value={form.city} onChange={set('city')} /></label>
          <label className="fl"><span>State</span><input className="field" value={form.state} onChange={set('state')} maxLength={2} /></label>
          <label className="fl"><span>ZIP</span><input className="field" value={form.zip} onChange={set('zip')} maxLength={10} /></label>
        </div>
        {officers?.officers.length ? (
          <label className="fl span-2"><span>Owner</span>
            <select className="field" value={form.owner_lo_id} onChange={set('owner_lo_id')}>
              <option value="">Unassigned</option>
              {officers.officers.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select></label>
        ) : null}
      </div>
    </ModalShell>
  )
}


function ModalShell({ children, onClose, onSave, busy, extra }: {
  children: React.ReactNode; onClose: () => void; onSave: () => void; busy: boolean
  /** A destructive action, kept at the far left so it is never next to Save. */
  extra?: React.ReactNode
}) {
  return (
    <Modal open title="Edit contact" onClose={onClose} width={540}
      footer={<>{extra}<span className="spacer" /><button className="btn" onClick={onClose}>Cancel</button><button className="btn primary" onClick={onSave} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button></>}>
      {children}
    </Modal>
  )
}


/** Edit the intake answers on an existing contact. Saves a full patch: the form
 *  starts from what is already stored, so nothing the officer didn't touch moves. */
/** Opt a contact out of one channel, or undo it.
 *
 *  Four channels, one control, because an officer asking "don't mail them" should
 *  not have to know that mail has no consent table and texting has TCPA behind it.
 *  Recording an opt-IN for texts still goes through the full dialog: that one needs
 *  a basis and an attestation, and a toggle is the wrong shape for evidence. */
function ChannelSwitch({ contactId, channel, open, consent, onOptIn, onSaved }: {
  contactId: number
  channel: Channel
  /** Whether the channel is actually usable — a green In beside "No phone on file"
   *  reads as a contradiction, because it is one. */
  open: boolean
  consent?: string
  onOptIn: () => void
  onSaved: () => void
}) {
  const [busy, setBusy] = useState(false)
  const toast = useToast()

  async function set(status: 'opted_out' | 'none') {
    setBusy(true)
    try {
      await api.recordConsent(contactId, { channel: channel === 'call' ? 'voice' : channel, status })
      toast(status === 'opted_out' ? 'Opted out' : 'Opt-out removed', { tone: 'positive' })
      onSaved()
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  const out = consent === 'opted_out'
  // In means "we may use this channel". For texts that needs an opt-in with a basis
  // behind it, so In opens the dialog; everywhere else it clears the opt-out.
  const goIn = () => (channel === 'sms' && consent !== 'opted_in' ? onOptIn() : set('none'))
  return (
    <span className="reach-toggle" role="group" aria-label={`${channel}: in or out`}>
      <button className={!out && open ? 'on' : ''} disabled={busy} onClick={goIn}
        title={channel === 'sms' && consent !== 'opted_in'
          ? 'Record the opt-in you hold' : 'They have not asked us to stop'}>In</button>
      <button className={out ? 'on out' : ''} disabled={busy} onClick={() => set('opted_out')}
        title="They have asked us not to use this channel">Out</button>
    </span>
  )
}

function GoalModal({ contact, vocabulary, onClose, onSaved }: {
  contact: ContactDetail
  vocabulary: ReturnType<typeof useVocabulary>
  onClose: () => void
  onSaved: (d: ContactDetail) => void
}) {
  const stored = (contact.profile ?? {}) as Record<string, unknown>
  const [value, setValue] = useState<ProfilePatch>({
    purpose: (stored.purpose as string) ?? '',
    timeline: (stored.timeline as string) ?? '',
    property_use: (stored.property_use as string) ?? '',
    price_or_value: (stored.price_or_value as number) ?? '',
    loan_amount: (stored.loan_amount as number) ?? '',
    down_payment: (stored.down_payment as number) ?? '',
    credit_band: (stored.credit_band as string) ?? '',
    first_time_buyer: (stored.first_time_buyer as boolean) ?? false,
    source: (stored.source as string) ?? '',
    referred_by_contact_id: (stored.referred_by_contact_id as number) ?? null,
    referred_by_person_key: (stored.referred_by_person_key as string) ?? null,
    referred_by_name: (stored.referred_by_name as string) ?? null,
    co_borrower_name: (stored.co_borrower_name as string) ?? '',
    co_borrower_email: (stored.co_borrower_email as string) ?? '',
    co_borrower_phone: (stored.co_borrower_phone as string) ?? '',
    goal_notes: (stored.goal_notes as string) ?? '',
  })
  const [busy, setBusy] = useState(false)
  const toast = useToast()

  async function save() {
    setBusy(true)
    try {
      const { referred_by_name: _ignored, ...patch } = value
      onSaved(await api.saveProfile(contact.id, patch))
      toast('Saved', { tone: 'positive' })
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open title="Loan goal" width={620} onClose={onClose}
      subtitle="What they want, when, and who sent them."
      footer={<>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
      </>}>
      <IntakeFields value={value} onChange={setValue} vocabulary={vocabulary} />
    </Modal>
  )
}


/** The card as a link, with the photo itself on hover — a thumbnail in a narrow
 *  column is too small to read and too big to ignore. The preview is positioned
 *  fixed so the card panel's own clipping can't cut it off. */
function BusinessCardLink({ card, name }: { card: { id: number; filename: string }; name: string | null }) {
  const [at, setAt] = useState<{ top: number; left: number } | null>(null)
  const ref = useRef<HTMLAnchorElement>(null)

  const show = () => {
    const r = ref.current?.getBoundingClientRect()
    if (!r) return
    const width = 320
    setAt({
      top: Math.min(Math.max(8, r.top - 40), window.innerHeight - 230),
      left: Math.min(r.right + 12, window.innerWidth - width - 12),
    })
  }

  return (
    <>
      <a ref={ref} className="social-link" href={api.attachmentUrl(card.id)} target="_blank" rel="noopener noreferrer"
        onMouseEnter={show} onFocus={show} onMouseLeave={() => setAt(null)} onBlur={() => setAt(null)}
        title="Open the full-size photo">
        <Icon name="badge" size={14} />
        <span>{card.filename}</span>
      </a>
      {at ? (
        <span className="card-peek" style={{ top: at.top, left: at.left }}>
          <img src={api.attachmentUrl(card.id)} alt={`${name ?? 'Contact'}'s business card`} />
        </span>
      ) : null}
    </>
  )
}
